/**
 * OCR Conflict Detection Utilities
 * 
 * Provides normalization and comparison functions to reduce false positive conflicts
 * when comparing OCR data with existing profile data.
 * 
 * Strategy: Field-specific normalization with configurable strictness levels
 */

/**
 * Calculate Levenshtein distance between two strings
 * Returns similarity percentage (0-100, where 100 = identical)
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity percentage (0-100)
 */
export function levenshteinSimilarity(str1, str2) {
  if (!str1 || !str2) return str1 === str2 ? 100 : 0;
  if (str1 === str2) return 100;
  
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Early exit for very different lengths
  if (Math.abs(len1 - len2) > Math.max(len1, len2) * 0.5) {
    return 0;
  }
  
  // Create matrix
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));
  
  // Initialize first row and column
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;
  
  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  const distance = matrix[len1][len2];
  const maxLen = Math.max(len1, len2);
  const similarity = ((maxLen - distance) / maxLen) * 100;
  
  return Math.round(similarity * 100) / 100; // Round to 2 decimal places
}

/**
 * Normalize name fields (case-insensitive)
 * @param {string} value - Name value
 * @returns {string} Normalized name (uppercase, trimmed)
 */
export function normalizeName(value) {
  if (!value || typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ').toUpperCase();
}

/**
 * Normalize unit number fields (strip special characters)
 * @param {string} value - Unit number value
 * @returns {string} Normalized unit number (alphanumeric only)
 */
export function normalizeUnitNumber(value) {
  if (!value || typeof value !== 'string') return '';
  // Strip #, -, commas, and whitespace, keep only alphanumeric
  return value.replace(/[#\-,.\s]/g, '').toUpperCase();
}

/**
 * Normalize postal code (numeric only)
 * @param {string} value - Postal code value
 * @returns {string} Normalized postal code (digits only)
 */
export function normalizePostalCode(value) {
  if (!value || typeof value !== 'string') return '';
  // Strip all non-numeric characters
  return value.replace(/\D/g, '');
}

/**
 * Normalize NRIC/FIN number (case-insensitive, strip spaces/hyphens)
 * @param {string} value - NRIC/FIN value
 * @returns {string} Normalized NRIC/FIN
 */
export function normalizeNricFin(value) {
  if (!value || typeof value !== 'string') return '';
  // Strip spaces and hyphens, uppercase
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Extract numeric components from address (block/unit numbers)
 * @param {string} address - Address string
 * @returns {Object} { blockNumber, unitNumber, remainingText }
 */
function extractAddressNumbers(address) {
  if (!address || typeof address !== 'string') {
    return { blockNumber: null, unitNumber: null, remainingText: '' };
  }
  
  const normalized = address.toUpperCase().trim();
  
  // Extract block number: "BLK 14", "BLOCK 14", "14", "#14", etc.
  const blockMatch = normalized.match(/(?:BLK|BLOCK)\s*(\d+)|^(\d+)(?=\s|$)/i);
  const blockNumber = blockMatch ? (blockMatch[1] || blockMatch[2]) : null;
  
  // Extract unit number: "#04-44", "04-44", "UNIT 04-44", etc.
  const unitMatch = normalized.match(/#?\s*(\d+)[-\s]*(\d+)|UNIT\s*(\d+)[-\s]*(\d+)/i);
  const unitNumber = unitMatch ? `${unitMatch[1] || unitMatch[3]}${unitMatch[2] || unitMatch[4]}` : null;
  
  // Remove block/unit numbers and common prefixes from remaining text
  let remaining = normalized
    .replace(/(?:BLK|BLOCK)\s*\d+/gi, '')
    .replace(/#?\s*\d+[-\s]*\d+/g, '')
    .replace(/UNIT\s*\d+[-\s]*\d+/gi, '')
    .replace(/[#\-,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  return { blockNumber, unitNumber, remainingText: remaining };
}

/**
 * Normalize address using hybrid approach
 * @param {string} address - Address string
 * @returns {Object} Normalized address components
 */
export function normalizeAddress(address) {
  if (!address || typeof address !== 'string') {
    return { blockNumber: null, unitNumber: null, normalizedText: '' };
  }
  
  const extracted = extractAddressNumbers(address);
  
  // Normalize common address words
  const normalizedText = extracted.remainingText
    .replace(/\b(BLK|BLOCK)\b/gi, 'BLK')
    .replace(/\b(STREET|ST|STR)\b/gi, 'STREET')
    .replace(/\b(ROAD|RD)\b/gi, 'ROAD')
    .replace(/\b(AVENUE|AVE|AV)\b/gi, 'AVENUE')
    .replace(/\b(DRIVE|DR)\b/gi, 'DRIVE')
    .replace(/\b(CRESCENT|CRES)\b/gi, 'CRESCENT')
    .replace(/\b(SINGAPORE|SG)\b/gi, 'SINGAPORE')
    .replace(/\s+/g, ' ')
    .trim();
  
  return {
    blockNumber: extracted.blockNumber,
    unitNumber: extracted.unitNumber,
    normalizedText
  };
}

/**
 * Compare two addresses using hybrid approach
 * @param {string} address1 - First address
 * @param {string} address2 - Second address
 * @param {number} fuzzyThreshold - Similarity threshold (0-100, default 90)
 * @returns {boolean} True if addresses match (no conflict)
 */
export function compareAddresses(address1, address2, fuzzyThreshold = 90) {
  if (!address1 || !address2) return address1 === address2;
  
  const norm1 = normalizeAddress(address1);
  const norm2 = normalizeAddress(address2);
  
  // Stage 1: Compare block numbers
  if (norm1.blockNumber && norm2.blockNumber) {
    if (norm1.blockNumber !== norm2.blockNumber) {
      return false; // Different block numbers = conflict
    }
  } else if (norm1.blockNumber || norm2.blockNumber) {
    // One has block number, other doesn't - check if remaining text contains the block number
    // This handles "BLK 14 DE SOUZA AVENUE" vs "14 DE SOUZA AVENUE"
    const blockNum = norm1.blockNumber || norm2.blockNumber;
    const otherText = norm1.blockNumber ? norm2.normalizedText : norm1.normalizedText;
    // If other text doesn't contain the block number, it might still match via fuzzy
    // Continue to fuzzy matching below
  }
  
  // Stage 2: Compare unit numbers (if both exist)
  if (norm1.unitNumber && norm2.unitNumber) {
    if (norm1.unitNumber !== norm2.unitNumber) {
      return false; // Different unit numbers = conflict
    }
  }
  
  // Stage 3: Compare remaining text using fuzzy matching
  if (!norm1.normalizedText && !norm2.normalizedText) {
    return true; // Both empty after extraction = match
  }
  
  if (!norm1.normalizedText || !norm2.normalizedText) {
    return false; // One empty, one not = conflict
  }
  
  const similarity = levenshteinSimilarity(norm1.normalizedText, norm2.normalizedText);
  return similarity >= fuzzyThreshold;
}

/**
 * Normalize date to ISO format (YYYY-MM-DD)
 * Assumes DD-MM-YYYY format for ambiguous dates (Singapore standard)
 * Uses formatDateForInput from dateUtils for proper timezone handling
 * 
 * @param {string|Date} value - Date value
 * @returns {string|null} Normalized date in YYYY-MM-DD format or null
 */
export function normalizeDate(value) {
  if (!value) return null;
  
  // If already in ISO format, return as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  
  try {
    // Try parsing as Date object
    const dateObj = new Date(value);
    if (isNaN(dateObj.getTime())) {
      // If Date constructor fails, try parsing DD-MM-YYYY format
      const ddmmyyyyMatch = String(value).match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
      if (ddmmyyyyMatch) {
        const day = ddmmyyyyMatch[1].padStart(2, '0');
        const month = ddmmyyyyMatch[2].padStart(2, '0');
        const year = ddmmyyyyMatch[3];
        // Validate: day <= 31, month <= 12
        if (parseInt(day) <= 31 && parseInt(month) <= 12) {
          return `${year}-${month}-${day}`;
        }
      }
      return null;
    }
    
    // Use Intl.DateTimeFormat to extract date parts in Singapore timezone
    const parts = new Intl.DateTimeFormat('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(dateObj);
    
    const partMap = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return `${partMap.year}-${partMap.month}-${partMap.day}`;
  } catch (error) {
    console.warn('Error normalizing date:', value, error);
    return null;
  }
}

/**
 * Normalize gender/sex field
 * Maps common variations to standard values (M/F/O)
 * 
 * @param {string} value - Gender value
 * @returns {string} Normalized gender (M/F/O or original if unknown)
 */
export function normalizeGender(value) {
  if (!value || typeof value !== 'string') return '';
  
  const upper = value.trim().toUpperCase();
  
  // Map common variations
  const genderMap = {
    'M': 'M',
    'MALE': 'M',
    'F': 'F',
    'FEMALE': 'F',
    'O': 'O',
    'OTHER': 'O',
    'OTHERS': 'O'
  };
  
  return genderMap[upper] || upper; // Return mapped value or original if not found
}

/**
 * Single source of truth mapping nationality variants -> canonical demonym.
 *
 * Keys are UPPERCASE and cover, for each nationality: the demonym, the country
 * name, the ISO 3166-1 alpha-2 code, and the alpha-3 code (the alpha-3 is what
 * appears in the passport MRZ "nationality" field, e.g. MMR, IND, BGD). This is
 * imported by the ID-card and passport OCR flows so conflict detection and
 * display stay consistent across the app.
 */
export const NATIONALITY_CANONICAL_MAP = {
  // Singaporean
  'SINGAPOREAN': 'Singaporean', 'SINGAPORE': 'Singaporean',
  'SINGAPORE CITIZEN': 'Singaporean', 'SG': 'Singaporean', 'SGP': 'Singaporean',
  // Malaysian
  'MALAYSIAN': 'Malaysian', 'MALAYSIA': 'Malaysian', 'MY': 'Malaysian', 'MYS': 'Malaysian',
  // Chinese
  'CHINESE': 'Chinese', 'CHINA': 'Chinese', 'CN': 'Chinese', 'CHN': 'Chinese',
  // Indian
  'INDIAN': 'Indian', 'INDIA': 'Indian', 'IN': 'Indian', 'IND': 'Indian',
  // Filipino
  'FILIPINO': 'Filipino', 'FILIPINA': 'Filipino', 'PHILIPPINES': 'Filipino',
  'PHILIPPINE': 'Filipino', 'PH': 'Filipino', 'PHL': 'Filipino',
  // Indonesian
  'INDONESIAN': 'Indonesian', 'INDONESIA': 'Indonesian', 'ID': 'Indonesian', 'IDN': 'Indonesian',
  // Thai
  'THAI': 'Thai', 'THAILAND': 'Thai', 'TH': 'Thai', 'THA': 'Thai',
  // Vietnamese
  'VIETNAMESE': 'Vietnamese', 'VIETNAM': 'Vietnamese', 'VIET NAM': 'Vietnamese',
  'VN': 'Vietnamese', 'VNM': 'Vietnamese',
  // Burmese (Myanmar)
  'BURMESE': 'Burmese', 'MYANMAR': 'Burmese', 'BURMA': 'Burmese', 'MM': 'Burmese', 'MMR': 'Burmese',
  // Bangladeshi
  'BANGLADESHI': 'Bangladeshi', 'BANGLADESH': 'Bangladeshi', 'BD': 'Bangladeshi', 'BGD': 'Bangladeshi',
  // Sri Lankan
  'SRI LANKAN': 'Sri Lankan', 'SRI LANKA': 'Sri Lankan', 'SRILANKAN': 'Sri Lankan',
  'SRILANKA': 'Sri Lankan', 'LK': 'Sri Lankan', 'LKA': 'Sri Lankan',
  // Japanese
  'JAPANESE': 'Japanese', 'JAPAN': 'Japanese', 'JP': 'Japanese', 'JPN': 'Japanese',
};

/**
 * Normalize a nationality value (country name, ISO alpha-2/alpha-3 code, or
 * demonym) to its canonical demonym spelling.
 *
 * @param {string} value - Nationality value (e.g. 'Myanmar', 'MMR', 'burmese')
 * @returns {string} Canonical demonym (e.g. 'Burmese'), or the trimmed original
 *                   if unrecognised.
 */
export function normalizeNationality(value) {
  if (!value || typeof value !== 'string') return '';

  // Collapse internal whitespace so 'VIET  NAM' / 'SRI  LANKA' still match.
  const normalized = value.trim().replace(/\s+/g, ' ');
  const upper = normalized.toUpperCase();
  return NATIONALITY_CANONICAL_MAP[upper] || normalized; // Return mapped or original
}

/**
 * Normalize race field (case-insensitive, map common variations)
 * @param {string} value - Race value
 * @returns {string} Normalized race
 */
export function normalizeRace(value) {
  if (!value || typeof value !== 'string') return '';
  
  const normalized = value.trim();
  
  // Map common variations
  const raceMap = {
    'CHINESE': 'chinese',
    'MALAY': 'malay',
    'INDIAN': 'indian',
    'EURASIAN': 'eurasian',
    'INDONESIAN': 'indonesian',
    'OTHERS': 'others',
    'OTHER': 'others',
    'NOT_DISCLOSED': 'not_disclosed',
    'PREFER NOT TO DISCLOSE': 'not_disclosed'
  };
  
  const upper = normalized.toUpperCase();
  return raceMap[upper] || normalized.toLowerCase(); // Return mapped or lowercase
}

/**
 * Normalize ID card type (case-insensitive, map common variations)
 * @param {string} value - ID card type value
 * @returns {string} Normalized ID card type
 */
export function normalizeIdCardType(value) {
  if (!value || typeof value !== 'string') return '';
  
  const normalized = value.trim().toLowerCase();
  
  // Map common variations
  const typeMap = {
    'citizen': 'citizen',
    'singapore citizen': 'citizen',
    'sg citizen': 'citizen',
    'pr': 'pr',
    'permanent resident': 'pr',
    'ep': 'ep',
    'employment pass': 'ep',
    's_pass': 's_pass',
    's pass': 's_pass',
    'spass': 's_pass',
    'wp': 'wp',
    'work permit': 'wp',
    'ltvp': 'ltvp',
    'long term visit pass': 'ltvp',
    'dp': 'dp',
    'dependant pass': 'dp',
    'other': 'other'
  };
  
  return typeMap[normalized] || normalized; // Return mapped or original
}

/**
 * Keep FIN and work-pass identifiers in their canonical fields.
 *
 * S Pass and Work Permit OCR can duplicate the visible pass number into the
 * NRIC/FIN field. A non-FIN value is moved to the visible work-pass field when
 * needed and removed from FIN so HR is never blocked by an off-screen conflict.
 *
 * @param {*} cardType - OCR/form ID-card type
 * @param {*} nricFinNumber - Candidate NRIC/FIN value
 * @param {*} workPassNumber - Candidate S Pass or Work Permit number
 * @returns {{nricFinNumber: string, workPassNumber: string}}
 */
export function normalizeIdCardIdentifiers(cardType, nricFinNumber, workPassNumber) {
  const normalizedType = normalizeIdCardType(String(cardType || ''));
  const nricFin = String(nricFinNumber || '').trim();
  const workPass = String(workPassNumber || '').trim();

  if (!['s_pass', 'wp'].includes(normalizedType) || !nricFin) {
    return { nricFinNumber: nricFin, workPassNumber: workPass };
  }

  const compactFin = normalizeNricFin(nricFin);
  if (/^[FGM]\d{7}[A-Z]$/.test(compactFin)) {
    return { nricFinNumber: compactFin, workPassNumber: workPass };
  }

  return {
    nricFinNumber: '',
    workPassNumber: workPass || nricFin,
  };
}

/**
 * Field-specific normalization configuration
 */
const FIELD_NORMALIZATION_CONFIG = {
  // Name fields
  'id_card_name': { normalizer: normalizeName, fuzzy: false },
  'occupation': { normalizer: normalizeName, fuzzy: false },
  
  // Unit number
  'id_card_residential_unit_number': { normalizer: normalizeUnitNumber, fuzzy: false },
  
  // Address (special handling)
  'id_card_address': { normalizer: null, fuzzy: true, threshold: 90, useAddressCompare: true },
  
  // Postal code
  'id_card_postal_code': { normalizer: normalizePostalCode, fuzzy: false },
  
  // NRIC/FIN
  'id_card_nric_fin_number': { normalizer: normalizeNricFin, fuzzy: false },
  'id_card_wp_number': { normalizer: normalizeNricFin, fuzzy: false },
  
  // Date fields
  'id_card_dob': { normalizer: normalizeDate, fuzzy: false },
  'id_card_issue_date': { normalizer: normalizeDate, fuzzy: false },
  'date_of_application': { normalizer: normalizeDate, fuzzy: false },
  'date_of_expiry': { normalizer: normalizeDate, fuzzy: false },
  
  // Select/dropdown fields
  'id_card_sex': { normalizer: normalizeGender, fuzzy: false },
  'id_card_nationality': { normalizer: normalizeNationality, fuzzy: false },
  'id_card_race': { normalizer: normalizeRace, fuzzy: false },
  'id_card_type': { normalizer: normalizeIdCardType, fuzzy: false },
  
  // Passport OCR fields
  'ocr_passport_first_name': { normalizer: normalizeName, fuzzy: false },
  'ocr_passport_last_name': { normalizer: normalizeName, fuzzy: false },
  'ocr_passport_number': { normalizer: normalizeNricFin, fuzzy: false },  // strip whitespace, uppercase
  'ocr_passport_nationality': { normalizer: normalizeNationality, fuzzy: false },
  'ocr_passport_date_of_birth': { normalizer: normalizeDate, fuzzy: false },
  'ocr_passport_gender': { normalizer: normalizeGender, fuzzy: false },
  'ocr_passport_expiry_date': { normalizer: normalizeDate, fuzzy: false }
};

/**
 * Compare two values using field-specific normalization
 * 
 * @param {string} fieldKey - Field key (e.g., 'id_card_name')
 * @param {*} value1 - First value (OCR data)
 * @param {*} value2 - Second value (existing profile data)
 * @returns {boolean} True if values match (no conflict), false if conflict
 */
export function compareFieldValues(fieldKey, value1, value2) {
  // Handle null/undefined/empty
  const isEmpty = (v) => v === null || v === undefined || v === '';
  if (isEmpty(value1) || isEmpty(value2)) {
    return value1 === value2; // Both empty = match, one empty = conflict
  }
  
  // Get field configuration
  const config = FIELD_NORMALIZATION_CONFIG[fieldKey];
  if (!config) {
    // No config = strict string comparison
    return String(value1).trim() === String(value2).trim();
  }
  
  // Special handling for addresses
  if (config.useAddressCompare) {
    return compareAddresses(value1, value2, config.threshold || 90);
  }
  
  // Use normalizer if available
  if (config.normalizer) {
    const norm1 = config.normalizer(value1);
    const norm2 = config.normalizer(value2);
    
    if (norm1 === null || norm2 === null) {
      return norm1 === norm2; // Both null = match, one null = conflict
    }
    
    // Exact match after normalization
    if (norm1 === norm2) {
      return true;
    }
    
    // If fuzzy matching enabled and no exact match, use similarity
    if (config.fuzzy && typeof norm1 === 'string' && typeof norm2 === 'string') {
      const similarity = levenshteinSimilarity(norm1, norm2);
      return similarity >= (config.threshold || 90);
    }
    
    return false; // No match
  }
  
  // Fallback: strict comparison
  return String(value1).trim() === String(value2).trim();
}

/**
 * Detect conflicts between OCR data and existing profile data
 * 
 * @param {Object} ocrData - OCR data object
 * @param {Object} existingProfile - Existing profile data object
 * @param {Object} fieldMapping - Mapping from OCR field keys to profile field keys
 * @returns {Object} Conflicts object with structure: { [ocrField]: { ocr, existing, profileField } }
 */
export function detectConflicts(ocrData, existingProfile, fieldMapping) {
  if (!ocrData || !existingProfile || typeof ocrData !== 'object' || typeof existingProfile !== 'object') {
    return {};
  }
  
  if (!fieldMapping || typeof fieldMapping !== 'object') {
    return {};
  }
  
  const conflicts = {};
  
  for (const [ocrField, profileField] of Object.entries(fieldMapping)) {
    const ocrValue = ocrData[ocrField];
    const existingValue = existingProfile[profileField];
    
    // Skip if either value is empty (no conflict if one is missing)
    if (ocrValue === null || ocrValue === undefined || ocrValue === '' ||
        existingValue === null || existingValue === undefined || existingValue === '') {
      continue;
    }
    
    // Compare using field-specific normalization
    const isMatch = compareFieldValues(ocrField, ocrValue, existingValue);
    
    if (!isMatch) {
      conflicts[ocrField] = {
        ocr: ocrValue,
        existing: existingValue,
        profileField: profileField
      };
    }
  }
  
  return conflicts;
}

/**
 * Restrict detected conflicts to fields the verification form actually renders.
 *
 * Banner count, conflict pickers, and approve-blocking must use the same keys.
 * Otherwise HR sees "2 Conflict(s) Detected" while only one picker is on
 * screen — e.g. a visit pass uploaded as ID card back still extracts DOB,
 * but the back form never renders that field.
 *
 * @param {Object} conflicts - Output of detectConflicts()
 * @param {Iterable<string>} visibleFieldKeys - Field keys currently rendered
 * @returns {Object} Conflicts limited to visible fields
 */
export function filterConflictsToVisibleFields(conflicts, visibleFieldKeys) {
  if (!conflicts || typeof conflicts !== 'object') {
    return {};
  }
  const visible = visibleFieldKeys instanceof Set
    ? visibleFieldKeys
    : new Set(visibleFieldKeys || []);
  return Object.fromEntries(
    Object.entries(conflicts).filter(([fieldKey]) => visible.has(fieldKey))
  );
}

